import Link from 'next/link'
import { Info } from 'lucide-react'
import {
  noticesForTool,
  methodologyHref,
  type ScoringNotice,
} from '@/lib/radar/scoring-notices'

/**
 * Dated notice explaining a scoring change, rendered next to the score it
 * moved. Deliberately a visible callout rather than a footnote: a customer
 * whose number dropped without touching their site should find the reason
 * where they find the number, not by hunting for a changelog.
 *
 * Copy lives in lib/radar/scoring-notices.ts so it stays editable without
 * touching this component.
 */
export function ScoringMethodologyNote({
  toolSlug,
  className = '',
}: {
  toolSlug: string
  className?: string
}) {
  const notices = noticesForTool(toolSlug)
  if (notices.length === 0) return null

  return (
    <div className={className}>
      {notices.map((notice: ScoringNotice) => (
        <section
          key={notice.id}
          aria-labelledby={`${notice.id}-heading`}
          className='border-l-2 border-primary bg-primary/5 px-4 py-3'
        >
          <div className='flex items-center gap-2 mb-1.5'>
            <Info
              size={14}
              aria-hidden='true'
              className='text-primary shrink-0'
            />
            <span
              id={`${notice.id}-heading`}
              className='text-xs font-bold text-foreground leading-none'
            >
              {notice.title} — {notice.displayDate}
            </span>
          </div>
          {notice.body.map((paragraph, i) => (
            <span
              key={i}
              className='block text-xs leading-relaxed text-muted-foreground mt-1.5'
            >
              {paragraph}
            </span>
          ))}
          <Link
            href={methodologyHref(notice.id)}
            className='inline-block mt-2 text-xs font-semibold text-primary hover:underline'
          >
            How scoring works
          </Link>
        </section>
      ))}
    </div>
  )
}
